import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { importProductsBulkAction } from "../../redux/actions/productActions";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { clearErrors } from "../../redux/actions/userActions";
import { PRODUCT_IMPORT_RESET } from "../../redux/constants/productConstants";

const PRODUCT_FIELDS = [
  "scan_code",
  "description",
  "unit_case",
  "department",
  "purchase_qty",
  "sold_qty",
  "adjust_qty",
  "current_qty",
  "each_cost",
  "each_retail"
];

const REQUIRED_FIELDS = [
  "scan_code",
  "description",
  "unit_case",
  "department"
];

const ImportProducts = () => {
  const dispatch = useDispatch();

  const [rawRows, setRawRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [hasHeader, setHasHeader] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [isImporting, setIsImporting] = useState(false);


  const {
    loading,
    error,
    inserted,
    skipped
  } = useSelector(state => state.product)


  useEffect(() => {
    document.title = "Import Products";
  }, []);

  /* =========================
     HANDLE FILE UPLOAD
  ========================== */
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      setRawRows(data);
      setMapping({});
    };

    reader.readAsBinaryString(file);
  };

  /* =========================
     BUILD HEADERS + PREVIEW
  ========================== */
  useEffect(() => {
    if (!rawRows.length) return;

    if (hasHeader) {
      setHeaders(rawRows[0]);
      setRows(rawRows.slice(1, 51)); // skip header
    } else {
      setHeaders(rawRows[0].map((_, i) => `Column ${i + 1}`));
      setRows(rawRows.slice(0, 50));
    }
  }, [rawRows, hasHeader]);

  /* =========================
     BUILD PRODUCTS
  ========================== */
  const buildProducts = () => {
    const products = [];

    const dataRows = hasHeader ? rawRows.slice(1) : rawRows;

    dataRows.forEach(row => {
      const product = {};

      Object.entries(mapping).forEach(([colIndex, field]) => {
        product[field] = row[colIndex];
      });

      const missing = REQUIRED_FIELDS.filter(
        f => !product[f]
      );

      if (missing.length === 0) {
        products.push(product);
      }
    });

    return products;
  };

  /* =========================
     IMPORT
  ========================== */
  const handleImport = () => {
    if (isImporting) return; // 🚫 prevent double click immediately
    const products = buildProducts();

    if (!products.length) {
      toast.error("No valid products found");
      setShowPopup(false)
      return;
    }

    setIsImporting(true);


    dispatch(importProductsBulkAction(products, setProgress));
  };

  const usedFields = Object.values(mapping);




  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(clearErrors())
      setShowPopup(false)
    }


  }, [dispatch, error])


  const handleView = () => {

    dispatch({ type: PRODUCT_IMPORT_RESET })

  }




  useEffect(() => {
    const Headerlinkks = document.querySelectorAll(".headerLinks");
    const element = document.getElementById("importProducts");

    Headerlinkks.forEach(el => el.classList.remove("activeHeaderLink"));
    element?.classList.add("activeHeaderLink");

    document.title = `Import Products - Admin Panel`;

    // auto focus scan input
  }, []);


  return (
    <Fragment>
      <section className="p bg-white rounded-[1vmax] shadow-1 p-[2vmax] ml-[1.5vmax] mr-[2.5vmax]">
        <h2 className="text-xl font-bold mb-4">Import Products</h2>

        {/* Upload */}
        <div className="flex items-center gap-4 mb-4">
          <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={hasHeader}
              onChange={() => setHasHeader(!hasHeader)}
            />
            Excel has header
          </label>
        </div>

        {/* Preview */}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <div className="flex bg-gray-100 min-w-max">
              {headers.map((h, i) => (
                <div key={i} className="w-48 border p-2">
                  <select
                    className="w-full mb-1 border rounded text-sm"
                    value={mapping[i] || ""}
                    onChange={(e) =>
                      setMapping(prev => ({ ...prev, [i]: e.target.value }))
                    }
                  >
                    <option value="">Skip</option>
                    {PRODUCT_FIELDS.filter(
                      f => !usedFields.includes(f) || mapping[i] === f
                    ).map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>

                  <div className="text-xs font-semibold text-center">{h}</div>
                </div>
              ))}
            </div>

            {rows.map((row, rIdx) => (
              <div key={rIdx} className="flex min-w-max">
                {headers.map((_, cIdx) => (
                  <div
                    key={cIdx}
                    className="w-48 border p-2 text-sm"
                  >
                    {row[cIdx] ?? ""}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {showPopup &&
          <div className="fixed w-[100vw] h-[100vh] top-0 left-0 bg-[#000000af] flex-center z-[9999999999]">
            <div className="bg-white p-[2vmax] rounded-xl">
              <p>Are you sure you want to import these products? <br /> Please make sure that the data is correct.</p>
              <br />
              <br />
              <div className="flex justify-end gap-[2vmax]">
                <button className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded" onClick={() => setShowPopup(false)}>No</button>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded disabled:opacity-50"
                  onClick={handleImport}
                  disabled={isImporting}
                >
                  {isImporting ? "Importing..." : "Yes"}
                </button>
              </div>
            </div>
          </div>

        }

        {/* Import Button */}
        {rows.length > 0 && (
          <button
            onClick={() => setShowPopup(true)}
            className="mt-6 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded"
          >
            Import Products
          </button>
        )}

        {/* Progress */}
        {progress > 0 && (
          <div className="fixed w-[100vw] h-[100vh] top-0 left-0 bg-white flex-center z-[9999999999] flex-col ">
            <div className="mt-4 bg-gray-500 rounded w-[50%] ">
              <div
                className="bg-green-600 text-white text-center rounded p-[0.2vmax]"
                style={{ width: `${progress}%` }}
              >
                {progress}%
              </div>
            </div>
            <div className="mt-[2vmax]">
              <span className="font-bold" >Products Import: &nbsp;</span>
              <span className="" >{inserted || 0}</span>
            </div>
            <div className="pt-[0.5vmax]">
              <span className="font-bold" >Products Skipped: &nbsp;</span>
              <span className="" >{skipped || 0}</span>
            </div>
            <div className="mt-[4vmax] w-[40%] text-center">
              Some products were automatically skipped because their UPCs already exist in the system, preventing duplicate entries.
            </div>
            {progress === 100 &&
              <Link to={'/product-catalog'} className="p-[0.6vmax_1vmax] rounded-2xl bg-[image:var(--gradient-2)] text-white mt-[2vmax]"
                onClick={() => handleView()}
                disabled={loading}
              >
                View Products
              </Link>

            }
          </div>
        )}
      </section>
    </Fragment>
  );
};

export default ImportProducts;
